import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { usePublicPackage, useSubmitPackageInquiry } from "@/hooks/usePublicPackage";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PublicFooter } from "@/components/website/PublicFooter";
import { PublicLayout } from "@/components/website/PublicLayout";
import { HeroImage } from "@/components/website/HeroImage";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";
import { usePricingTypes, getUnitLabel } from "@/hooks/usePricingTypes";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { publicPackageUrl } from "@/lib/public-urls";
import { ogImageUrl, plainText, packageProductJsonLd } from "@/lib/seo";
import { Users, ChefHat, AlertTriangle } from "lucide-react";
import { z } from "zod";
import { NotFoundError } from "@/lib/queryErrors";

const inquirySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  event_date: z.string().optional().or(z.literal("")),
  guest_count: z.coerce.number().int().min(1).max(10000).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

const EMPTY_FORM = { name: "", email: "", phone: "", event_date: "", guest_count: "", message: "" };

export default function PublicPackagePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: pkg, isLoading, error, refetch, isFetching } = usePublicPackage(slug || "");
  const submitInquiry = useSubmitPackageInquiry();
  const { data: dietaryTags } = useDietaryTags();
  const { data: pricingTypes } = usePricingTypes();
  const formRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  const [showSticky, setShowSticky] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Honeypot field — bots fill every visible input; this one is hidden from humans.
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const isCustomerChoice = pkg?.selection_mode === "customer_choice";
  const sortedSlots = useMemo(() => [...(pkg?.category_slots || [])].sort((a, b) => a.sort_order - b.sort_order), [pkg?.category_slots]);
  const categoryIds = useMemo(() => sortedSlots.map((s) => s.category_id), [sortedSlots]);

  const seoDescription = useMemo(() => plainText(pkg?.description), [pkg?.description]);
  const seoImage = useMemo(() => ogImageUrl(pkg?.og_image_url ?? pkg?.cover_image_url), [pkg?.og_image_url, pkg?.cover_image_url]);
  const seoJsonLd = useMemo(() => (pkg ? packageProductJsonLd(pkg) : undefined), [pkg]);

  useDocumentMeta({
    title: pkg ? `${pkg.name} — Tristano's Catering` : undefined,
    description: seoDescription || undefined,
    canonical: slug ? publicPackageUrl(slug) : undefined,
    ogImage: seoImage,
    ogType: "product",
    twitterCard: "summary_large_image",
    jsonLd: seoJsonLd,
  });

  // Fetch active menu items for customer choice categories
  const { data: slotMenuItems } = useQuery({
    queryKey: ["slot_menu_items", categoryIds],
    enabled: !!isCustomerChoice && categoryIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("menu_items")
        .select("id, name, description, dietary_tags, category_id")
        .in("category_id", categoryIds)
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return (data || []) as { id: string; name: string; description: string | null; dietary_tags: string[]; category_id: string }[];
    },
  });

  const slotItemsMap = useMemo(() => {
    if (!slotMenuItems) return {};
    const map: Record<string, typeof slotMenuItems> = {};
    for (const slot of sortedSlots) {
      const excludedIds = new Set((slot.exclusions || []).map((e) => e.menu_item_id));
      map[slot.id] = slotMenuItems.filter(
        (mi) => mi.category_id === slot.category_id && !excludedIds.has(mi.id)
      );
    }
    return map;
  }, [slotMenuItems, sortedSlots]);

  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Focus the success heading so screen readers announce it and keyboard focus lands there.
  useEffect(() => {
    if (submitted) {
      successHeadingRef.current?.focus();
    }
  }, [submitted]);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
    // Defer focus until after smooth-scroll completes so the browser doesn't fight us.
    window.setTimeout(() => firstFieldRef.current?.focus(), 400);
  }, []);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setHoneypot("");
  }, []);

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Escape" && !submitInquiry.isPending) {
      e.preventDefault();
      resetForm();
    }
  }, [resetForm, submitInquiry.isPending]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Silently drop bot submissions that filled the honeypot.
    if (honeypot.trim() !== "") {
      setSubmitted(true);
      setForm(EMPTY_FORM);
      setHoneypot("");
      return;
    }
    setErrors({});
    const result = inquirySchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    if (!pkg) return;
    submitInquiry.mutate(
      {
        package_id: pkg.id,
        name: result.data.name,
        email: result.data.email,
        phone: result.data.phone || undefined,
        event_date: result.data.event_date || undefined,
        guest_count: typeof result.data.guest_count === "number" ? result.data.guest_count : undefined,
        message: typeof result.data.message === "string" ? result.data.message : undefined,
        idempotency_key: idempotencyKeyRef.current,
        content_category: pkg.slug,
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          setForm(EMPTY_FORM);
          setHoneypot("");
          // Fresh key for "Submit Another Inquiry" flow
          idempotencyKeyRef.current = crypto.randomUUID();
        },
      }
    );
  };

  if (isLoading) {
    return <PublicLayout className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">Loading package…</p></PublicLayout>;
  }

  // Differentiate "this slug doesn't exist" from "the service is down". A
  // network hiccup should not look identical to a 404 — users think it means
  // the content was deleted when it's actually recoverable with a retry.
  if (error && !(error instanceof NotFoundError)) {
    return (
      <PublicLayout className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
            We're having trouble loading this package
          </h1>
          <p className="text-muted-foreground mb-6">Please try again in a moment.</p>
          <Button onClick={() => refetch()} disabled={isFetching} className="min-h-[44px]">
            {isFetching ? "Retrying…" : "Try again"}
          </Button>
        </div>
      </PublicLayout>
    );
  }

  if (!pkg) {
    return (
      <PublicLayout className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Package Not Available</h1>
          <p className="text-muted-foreground mb-6">This package is no longer available.</p>
          <Button asChild className="min-h-[44px]">
            <Link to="/packages">Browse all packages</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const unitLabel = getUnitLabel(pricingTypes, pkg.pricing_type);
  const isSubmitting = submitInquiry.isPending;

  // Group fixed items by category
  const groupedItems = (() => {
    if (isCustomerChoice || !pkg.items?.length) return [];
    const groups: Record<string, { categoryName: string; items: typeof pkg.items }> = {};
    for (const item of pkg.items!) {
      const catName = item.menu_item?.category?.name || "Other";
      const catId = item.menu_item?.category?.id || "other";
      if (!groups[catId]) groups[catId] = { categoryName: catName, items: [] };
      groups[catId].items!.push(item);
    }
    return Object.values(groups);
  })();

  return (
    <PublicLayout className="min-h-screen bg-background">
      {/* Sticky CTA */}
      {showSticky && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm shadow-md">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <span className="text-primary-foreground font-logo font-semibold text-sm truncate">{pkg.name}</span>
            <Button size="sm" variant="secondary" onClick={scrollToForm} className="shrink-0">
              {pkg.cta_text}
            </Button>
          </div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="focus:outline-none">
      {/* Hero */}
      <div
        className="relative w-full flex flex-col overflow-hidden"
        style={{
          // minHeight is a floor: short content centres within it, long content grows the hero.
          minHeight: pkg.cover_image_url ? "380px" : "260px",
          background: pkg.cover_image_url ? undefined : `hsl(var(--primary))`,
        }}
      >
        {/* Background image via <picture> for modern format + responsive srcSet */}
        {pkg.cover_image_url && (
          <>
            <HeroImage
              src={pkg.cover_image_url}
              alt={pkg.name}
              isAboveFold
            />
            {/* Dark gradient overlay — replaces the old CSS linear-gradient on background shorthand */}
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to bottom, hsl(var(--foreground) / 0.5), hsl(var(--foreground) / 0.7))",
              }}
            />
          </>
        )}
        <div className="relative flex-1 flex flex-col items-center justify-center text-center px-4 py-6">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">{pkg.name}</h1>
          {pkg.description && (
            <p className="text-white/80 max-w-2xl text-base md:text-lg font-secondary whitespace-pre-line">{pkg.description}</p>
          )}

          {/* Pricing + guest info */}
          <div className="flex items-center gap-4 mt-6">
            {pkg.show_prices && (
              <span className="text-2xl font-bold text-white">
                ${Number(pkg.price).toFixed(2)}
                <span className="text-base font-normal text-white/70">/{unitLabel}</span>
              </span>
            )}
            {(pkg.min_guests || pkg.max_guests) && (
              <span className="flex items-center gap-1.5 text-white/70 text-sm">
                <Users className="h-4 w-4" />
                {pkg.min_guests && pkg.max_guests
                  ? `${pkg.min_guests}–${pkg.max_guests} guests`
                  : pkg.min_guests
                  ? `${pkg.min_guests}+ guests`
                  : `Up to ${pkg.max_guests} guests`}
              </span>
            )}
          </div>

          {/* Event type badges */}
          {(pkg.event_types?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 justify-center">
              {pkg.event_types!.map((et) => (
                <Badge key={et.id} variant="secondary" className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                  {et.event_type?.name}
                </Badge>
              ))}
            </div>
          )}

          <Button size="lg" variant="secondary" className="mt-6" onClick={scrollToForm}>
            {pkg.cta_text}
          </Button>
        </div>
      </div>

      {/* Contents */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-10">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground flex items-center justify-center gap-2">
            <ChefHat className="h-6 w-6" />
            {isCustomerChoice ? "Your Selections" : "What's Included"}
          </h2>
          {isCustomerChoice && (
            <p className="text-muted-foreground mt-2 font-secondary">
              Customize your menu by choosing from the categories below.
            </p>
          )}
        </div>

        {isCustomerChoice ? (
          <div className="space-y-8">
            {sortedSlots.map((slot) => {
              const items = slotItemsMap[slot.id] || [];
              return (
                <section key={slot.id}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-heading font-bold text-foreground">
                      {slot.label_override || slot.category?.name}
                    </h3>
                    <Badge variant="secondary">
                      Choose {slot.quantity}
                    </Badge>
                  </div>
                  {items.length > 0 ? (
                    <div className="divide-y divide-border">
                      {items.map((mi) => (
                        <div key={mi.id} className="flex items-start justify-between py-4 gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-foreground">{mi.name}</h4>
                              {(mi.dietary_tags || []).map((tag: string) => (
                                <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                  {getDietaryTagLabel(dietaryTags, tag)}
                                </Badge>
                              ))}
                            </div>
                            {mi.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{mi.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No items available in this category.</p>
                  )}
                </section>
              );
            })}
          </div>
        ) : (
          <div className="space-y-8">
            {groupedItems.map((group, idx) => (
              <section key={idx}>
                <h3 className="text-xl font-heading font-bold text-foreground mb-4">{group.categoryName}</h3>
                <div className="divide-y divide-border">
                  {group.items!.map((item) => {
                    const mi = item.menu_item;
                    if (!mi) return null;
                    return (
                      <div key={item.id} className="flex items-start justify-between py-4 gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-foreground">
                              {item.quantity > 1 && <span className="text-muted-foreground">{item.quantity}× </span>}
                              {mi.name}
                            </h4>
                            {(mi.dietary_tags || []).map((tag: string) => (
                              <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                {getDietaryTagLabel(dietaryTags, tag)}
                              </Badge>
                            ))}
                          </div>
                          {mi.description && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{mi.description}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Lead Capture Form */}
      <div
        ref={formRef}
        className="bg-muted py-16"
        style={{
          // Prevent the sticky CTA from covering the form heading after scrollIntoView.
          scrollMarginTop: "64px",
        }}
      >
        <div className="max-w-xl mx-auto px-4">
          {submitted ? (
            <div role="alert" aria-live="polite" className="text-center py-12">
              <h2
                ref={successHeadingRef}
                tabIndex={-1}
                className="text-2xl font-heading font-bold text-foreground mb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
              >
                Thank You!
              </h2>
              <p className="text-muted-foreground">We've received your inquiry and will be in touch shortly.</p>
              <Button className="mt-6" onClick={() => setSubmitted(false)}>Submit Another Inquiry</Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">{pkg.cta_text}</h2>
                <p className="text-muted-foreground mt-1 font-secondary">
                  Tell us about your event and we'll get back to you with a custom quote.
                </p>
              </div>
              <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-4" noValidate>
                {/* Honeypot — hidden from humans, visible to naive bots. */}
                <div aria-hidden="true" className="sr-only">
                  <Label htmlFor="pkg-website">Website</Label>
                  <input
                    id="pkg-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <fieldset disabled={isSubmitting} className="space-y-4 disabled:opacity-70">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="pkg-name">Name *</Label>
                      <Input
                        id="pkg-name"
                        ref={firstFieldRef}
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                      {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="pkg-email">Email *</Label>
                      <Input
                        id="pkg-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="pkg-phone">Phone</Label>
                      <Input
                        id="pkg-phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pkg-event-date">Event Date</Label>
                      <Input
                        id="pkg-event-date"
                        type="date"
                        value={form.event_date}
                        onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="pkg-guest-count">Guest Count</Label>
                      <Input
                        id="pkg-guest-count"
                        type="number"
                        min={1}
                        value={form.guest_count}
                        onChange={(e) => setForm({ ...form, guest_count: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="pkg-message">Message</Label>
                    <Textarea
                      id="pkg-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      placeholder="Tell us about your event..."
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : pkg.cta_text}
                  </Button>
                </fieldset>
              </form>
            </>
          )}
        </div>
      </div>
      </main>

      <PublicFooter />
    </PublicLayout>
  );
}
